/**
 * Claude AI integration for flow summaries
 */
import { supabase } from './supabaseClient';

// Setup type definitions for the Claude API response
interface AnthropicMessage {
  role: 'user' | 'assistant';
  content: string | Array<AnthropicContent>;
}

interface AnthropicContent {
  type: 'text' | 'image';
  text?: string;
  source?: {
    type: 'base64';
    media_type: string;
    data: string;
  };
}

interface AnthropicResponse {
  id: string;
  type: string;
  role: string;
  content: Array<{
    type: string;
    text: string;
  }>;
  model: string;
  stop_reason: string;
  stop_sequence: string | null;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

interface ClaudeEdgeFunctionRequest {
  prompt: string;
  flow_id: string;
  request_type: string;
  model?: string;
  max_tokens?: number;
  project_id?: string;
  use_knowledge_base?: boolean;
  max_knowledge_chunks?: number;
}

interface ApiUsageStats {
  total_requests: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  successful_requests: number;
  failed_requests: number;
  total_cost?: number;
  avg_duration_ms?: number;
}

/**
 * Generate a flow summary using Claude
 */
export async function generateFlowSummary(flowData: any): Promise<string> {
  try {
    // Add prominent browser console logs to track Claude summary generation
    console.log('%c[AI SUMMARY START] Generating summary for flow: ' + flowData.name, 
      'background: #4c1d95; color: white; padding: 8px; border-radius: 4px; font-size: 14px;');
    
    console.log('Generating flow summary with Claude for flow:', flowData.name);
    console.log('Flow data structure:', Object.keys(flowData));
    
    // Show elapsed time for this summary request
    const startTime = new Date();
    
    // Load the prompt template
    const promptTemplate = await fetchPromptTemplate();
    console.log('Loaded prompt template, length:', promptTemplate.length);
    
    // Prepare the flow data for the prompt
    const flowSummaryData = JSON.stringify({
      flowName: flowData.name,
      flowType: flowData.process_type,
      description: flowData.description || 'No description provided',
      status: flowData.status,
      elements: flowData.flow_elements?.records || [],
      variables: flowData.flow_variables?.records || [],
      fieldReferences: flowData.field_references || []
    }, null, 2);
    
    console.log('Prepared flow summary data with elements:', 
      flowData.flow_elements?.records?.length || 0, 
      'variables:', flowData.flow_variables?.records?.length || 0);
    
    // Build the complete prompt
    const prompt = `${promptTemplate}\n\n## Flow Data\n\`\`\`json\n${flowSummaryData}\n\`\`\``;
    console.log('Built complete prompt, length:', prompt.length);
    
    console.log('Calling Claude API for flow_id:', flowData.sf_flow_id);
    
    // Call the Claude API through the Vercel API route
    console.log('%c[AI SUMMARY API CALL] Sending request to Claude API for flow: ' + flowData.name, 
      'background: #047857; color: white; padding: 8px; border-radius: 4px; font-size: 14px;');
    
    // Add project ID and knowledge base configuration if available
    const projectId = import.meta.env.VITE_ANTHROPIC_PROJECT_ID || null;
    const useKnowledgeBase = import.meta.env.VITE_ANTHROPIC_KNOWLEDGE_ENABLED === 'true';
    const maxKnowledgeChunks = parseInt(import.meta.env.VITE_ANTHROPIC_KNOWLEDGE_MAX_CHUNKS || '10', 10);
    
    if (projectId) {
      console.log('%c[CLAUDE PROJECT] Using Project Knowledge Base with ID: ' + projectId, 
        'background: #047857; color: white; padding: 6px; border-radius: 3px; font-size: 13px;');
    }
    
    const response = await callClaudeAPI({
      prompt: prompt,
      flow_id: flowData.sf_flow_id,
      request_type: 'flow_summary', // This maps to feature_type in ai_usage_logs
      model: 'claude-3-opus-20240229',
      max_tokens: 4000,
      project_id: projectId,
      use_knowledge_base: useKnowledgeBase,
      max_knowledge_chunks: maxKnowledgeChunks
    });
    
    console.log('%c[AI SUMMARY API RESPONSE] Received response from Claude API for flow: ' + flowData.name, 
      'background: #0e7490; color: white; padding: 8px; border-radius: 4px; font-size: 14px;');
    console.log('Received response from Claude API:', typeof response, Object.keys(response));
    
    // Extract the summary from the response
    const summary = extractSummary(response);
    console.log('Extracted summary, length:', summary.length);
    
    // Calculate and log elapsed time
    const endTime = new Date();
    const elapsedSeconds = (endTime.getTime() - startTime.getTime()) / 1000;
    
    console.log('%c[AI SUMMARY COMPLETE] Successfully generated summary for flow: ' + flowData.name + 
      ` (${elapsedSeconds.toFixed(2)}s)`, 
      'background: #065f46; color: white; padding: 8px; border-radius: 4px; font-size: 14px;');
      
    return summary;
  } catch (error) {
    console.error('Error generating flow summary with Claude:', error);
    return `Error generating flow summary: ${error.message}`;
  }
}

/**
 * Interface for the summary status
 */
export interface SummaryStatus {
  inProgress: boolean;
  totalFlows: number;
  flowsWithoutSummaries: number;
  currentBatchSize: number;
  completedInBatch: number;
  failedInBatch: number;
  lastSyncTime: string | null;
  lastBatchTime: string | null;
  currentlyProcessing: Array<{
    flowId: string;
    flowName: string;
    startTime: string;
  }>;
  recentlyCompleted: Array<{
    flowId: string;
    flowName: string;
    startTime: string;
    endTime: string;
    success: boolean;
    error?: string;
  }>;
  flowsWithoutSummariesInDB?: number;
  recentSummaryLogs?: Array<any>;
}

/**
 * Check the status of flow summarization
 * This allows the client to display progress information about flow summarization
 */
export async function getSummaryStatus(): Promise<any | null> {
  try {
    console.log('Checking summary status...');
    
    const response = await fetch('/api/claude/summary-status', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      console.error('Error checking summary status:', response.status, response.statusText);
      return null;
    }
    
    const data = await response.json();
    console.log('Summary status response:', data);
    
    // Check if there's an error in the response
    if (data.error) {
      console.error('Error in summary status response:', data.error);
      return null;
    }
    
    // Return the full data object as the status
    return data;
  } catch (error) {
    console.error('Error checking summary status:', error);
    return null;
  }
}

/**
 * Fetch the prompt template from the local file
 */
async function fetchPromptTemplate(): Promise<string> {
  try {
    const response = await fetch('/prompts/summarizeFlow.md');
    if (!response.ok) {
      throw new Error(`Failed to fetch prompt template: ${response.status}`);
    }
    return await response.text();
  } catch (error) {
    console.error('Error fetching prompt template:', error);
    // Fallback to basic prompt
    return 'Please analyze this Salesforce Flow and provide a complete summary in markdown format.';
  }
}

/**
 * Call the Claude API via the Vercel API route
 */
async function callClaudeAPI(request: ClaudeEdgeFunctionRequest): Promise<AnthropicResponse> {
  // Add prominent logging for API call
  console.log('%c[CLAUDE API REQUEST] Initiating API request to Claude', 
    'background: #1e40af; color: white; padding: 6px; border-radius: 3px; font-size: 13px;');
  
  console.log('callClaudeAPI started with request:', {...request, prompt: request.prompt.substring(0, 100) + '...'});
  
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  console.log('Got session, authenticated:', !!session?.access_token);
  
  if (!session) {
    throw new Error('No active session. User must be authenticated to use Claude API.');
  }
  
  // Use the Vercel API route instead of Supabase Edge Function
  const CLAUDE_API_URL = '/api/claude';
  console.log('Claude API URL:', CLAUDE_API_URL);
  
  try {
    console.log('Sending request to Claude API...');
    const response = await fetch(CLAUDE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify(request)
    });
    
    console.log('Received response from Claude API, status:', response.status);
    
    if (!response.ok) {
      const errorDetail = await response.text();
      console.error('Claude API error:', errorDetail);
      console.log('%c[CLAUDE API ERROR] API request failed with status: ' + response.status, 
        'background: #b91c1c; color: white; padding: 6px; border-radius: 3px; font-size: 13px;');
      throw new Error(`Claude API call failed with status ${response.status}: ${errorDetail}`);
    }
    
    console.log('%c[CLAUDE API SUCCESS] API request successful with status: ' + response.status, 
      'background: #15803d; color: white; padding: 6px; border-radius: 3px; font-size: 13px;');
    
    const responseData = await response.json();
    console.log('Parsed response JSON, structure:', Object.keys(responseData));
    return responseData;
  } catch (error) {
    console.error('Error in callClaudeAPI:', error);
    throw error;
  }
}

/**
 * Extract the summary from the Claude API response
 */
function extractSummary(response: AnthropicResponse): string {
  console.log('Extracting summary from response:', response);
  
  // Check if it's the original format
  if (response.content && Array.isArray(response.content)) {
    console.log('Found standard Anthropic API response format');
    // Combine all text parts from the response
    return response.content
      .filter((item: any) => item.type === 'text')
      .map((item: any) => item.text)
      .join('');
  }
  
  // Check if it's our custom Edge Function format
  if (typeof response.content === 'string') {
    console.log('Found Edge Function custom response format with string content');
    return response.content;
  }
  
  // Check if the Edge Function is returning a different format
  if (response.id && response.model) {
    // This is likely the raw Anthropic response passed through
    console.log('Found raw Anthropic response format');
    if (Array.isArray(response.content)) {
      return response.content
        .filter((item: any) => item.type === 'text')
        .map((item: any) => item.text)
        .join('');
    }
  }
  
  console.log('No recognized response format, dumping response:', JSON.stringify(response, null, 2));
  return 'No summary was generated. Unexpected response format.';
}

/**
 * Get the user's API usage statistics
 */
export async function getUserApiUsage(startDate?: Date, endDate?: Date): Promise<ApiUsageStats | null> {
  try {
    // Use RPC function to get consolidated AI usage statistics
    // Assuming there's an RPC function that queries ai_usage_logs and returns stats
    const { data, error } = await supabase.rpc('get_ai_usage_stats', {
      start_date: startDate?.toISOString() || null,
      end_date: endDate?.toISOString() || null,
      feature_type: 'flow_summary' // Filter to just Claude flow summaries
    });
    
    if (error) {
      console.error('Error getting AI usage stats:', error);
      
      // Fallback - query the ai_usage_logs table directly
      try {
        const { data: rawData, error: queryError } = await supabase
          .from('ai_usage_logs')
          .select(`
            count(*) as total_requests,
            sum(input_tokens) as total_prompt_tokens,
            sum(output_tokens) as total_completion_tokens,
            sum(input_tokens + output_tokens) as total_tokens,
            sum(case when success = true then 1 else 0 end) as successful_requests,
            sum(case when success = false then 1 else 0 end) as failed_requests
          `)
          .eq('feature_type', 'flow_summary')
          .gte('created_at', startDate?.toISOString() || new Date(0).toISOString())
          .lte('created_at', endDate?.toISOString() || new Date().toISOString())
          .single();
          
        if (queryError) {
          console.error('Error in fallback query for API usage stats:', queryError);
          return null;
        }
        
        return rawData;
      } catch (fallbackError) {
        console.error('Error in fallback query for API usage stats:', fallbackError);
        return null;
      }
    }
    
    return data[0];
  } catch (error) {
    console.error('Error fetching API usage stats:', error);
    return null;
  }
}

/**
 * Get the user's recent AI usage history for Claude flow summaries
 */
export async function getUserApiUsageHistory(limit: number = 10): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('ai_usage_logs')
      .select(`
        id,
        account_id,
        user_id,
        flow_id,
        feature_type,
        model_used,
        input_tokens,
        output_tokens,
        duration_ms,
        estimated_cost,
        success,
        error_message,
        created_at
      `)
      .eq('feature_type', 'flow_summary')
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Error getting API usage history:', error);
      return [];
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching API usage history:', error);
    return [];
  }
}